import { useEffect } from "react";

/** Right-hand sheet used across PaisaCX for trade tickets and forms. */
export function Drawer({ title, subtitle, onClose, children, footer, testId, width = "max-w-2xl" }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid={testId}>
      <button type="button" aria-label="Close" className="absolute inset-0 bg-background/70 backdrop-blur-sm" onClick={onClose} />
      <aside className={`page-fade relative flex h-full w-full ${width} flex-col border-l border-border bg-surface`}>
        <div className="flex items-start justify-between gap-4 p-6 pb-0">
          <div className="min-w-0">
            <h2 className="text-xl font-semibold">{title}</h2>
            {subtitle ? <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid={testId ? `${testId}-close` : undefined}
            className="shrink-0 rounded-lg border border-border px-2.5 py-1 text-sm text-muted-foreground"
          >
            Close
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-6">{children}</div>
        {footer ? <div className="border-t border-border p-4">{footer}</div> : null}
      </aside>
    </div>
  );
}

export function Field({ label, hint, children, htmlFor }) {
  return (
    <div>
      <label className="text-xs text-muted-foreground" htmlFor={htmlFor}>
        {label}
      </label>
      <div className="mt-1.5">{children}</div>
      {hint ? <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

export const inputCls =
  "w-full rounded-lg border border-border bg-surface-2 px-3 py-2.5 text-sm outline-none focus:border-primary disabled:opacity-50";

export function Segmented({ options, value, onChange, size = "xs", testIdPrefix }) {
  return (
    <div className="flex max-w-full gap-1 overflow-x-auto rounded-lg border border-border bg-surface-2 p-1">
      {options.map((o) => {
        const opt = typeof o === "string" ? { value: o, label: o } : o;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            data-testid={testIdPrefix ? `${testIdPrefix}-${opt.value}` : undefined}
            className={`whitespace-nowrap rounded-md px-3 py-1.5 ${size === "sm" ? "text-sm" : "text-xs"} transition ${
              value === opt.value ? "brand-gradient text-primary-foreground" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

export function Notice({ tone = "info", children, className = "", ...rest }) {
  const tones = {
    info: "border-primary/30 bg-primary/10 text-primary",
    warn: "border-warning/40 bg-warning/10 text-warning",
    error: "border-down/40 bg-down/10 text-down",
    ok: "border-up/40 bg-up/10 text-up",
  };
  return (
    <div {...rest} className={`rounded-lg border px-3 py-2 text-sm ${tones[tone]} ${className}`}>
      {children}
    </div>
  );
}

export function Toggle({ on, onChange, label, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={onChange}
      className={`h-5 w-9 shrink-0 rounded-full p-0.5 transition disabled:opacity-50 ${on ? "brand-gradient" : "border border-border bg-surface-2"}`}
    >
      <span className={`block h-4 w-4 rounded-full bg-foreground transition-transform ${on ? "translate-x-4" : ""}`} />
    </button>
  );
}

export function EmptyRow({ children }) {
  return <p className="px-4 py-14 text-center text-sm text-muted-foreground">{children}</p>;
}

export function SkeletonRows({ count = 3, h = "h-10" }) {
  return (
    <div className="space-y-2 p-4">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className={`skeleton ${h} w-full`} />
      ))}
    </div>
  );
}
